import { type PropsWithChildren } from "react";

import clsx from "clsx";

import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { type INBAccountingMode } from "@north-beam/nb-common";

interface Props {
  label?: string;
  value: INBAccountingMode;
  disabled?: boolean;
  onChange: (value: INBAccountingMode) => void;
  accrualOnly?: boolean;
}

export const AccountingModeSwitch = ({
  label = "ACCOUNTING MODE",
  value,
  disabled,
  onChange,
  accrualOnly,
}: Props) => {
  return (
    <div
      className={clsx("text-v2-primary text-sm", {
        "pointer-events-none": disabled,
      })}
    >
      <label
        className={clsx(
          "label text-xs leading-5 flex gap-1 mb-[3px]",
          disabled && "text-disabled",
        )}
      >
        {label}
        <LearnMoreTooltip
          title="Accounting Mode"
          content={
            "Northbeam's accounting models determine the amount of " +
            "revenue and transactional credit given on a particular day"
          }
          link="https://docs.northbeam.io/docs/accounting-modes"
        />
      </label>
      <div className={clsx({ "opacity-60": disabled })}>
        <Button
          className={clsx("border-l rounded-l-md", {
            "border-r rounded-r-md": accrualOnly,
          })}
          active={value === "accrual"}
          disabled={disabled}
          onClick={() => onChange("accrual")}
        >
          Accrual
        </Button>
        {!accrualOnly && (
          <Button
            className="border-r rounded-r-md"
            active={value === "cash"}
            disabled={disabled}
            onClick={() => onChange("cash")}
          >
            Cash
          </Button>
        )}
      </div>
    </div>
  );
};

const Button = ({
  children,
  active,
  className,
  disabled,
  onClick,
}: PropsWithChildren<{
  active: boolean;
  className: string;
  disabled?: boolean;
  onClick: () => void;
}>) => {
  return (
    <button
      className={clsx("h-8 border-y px-3 min-w-[120px]", className, {
        "text-white": active,
        "border-v2-action bg-v2-action": active && !disabled,
        "border-v2-secondary bg-gray": active && disabled,
        "border-v2-secondary hover:border-v2-action hover:bg-v2-secondary":
          !active,
      })}
      onClick={onClick}
    >
      {children}
    </button>
  );
};
